import { useState, type ReactNode, type RefObject } from "react";
import { ArrowRight, CalendarDays, MoreHorizontal, Trash2, X } from "lucide-react";
import { Dialog, Popover } from "react-aria-components";
import { cn } from "@/utils/helpers";
import { useCoShellPortal } from "../../CoShellPortalContext";
import StageTrack from "../components/StageTrack";
import type { PrimaryAction } from "../postState";
import { dayMonthShort, pillarHue, stageWord, type CalendarPost, type DayCell } from "./calendarModel";
import { formatLabel } from "./CalendarPostCard";
import { ChannelMarks, TurnLine } from "./CalendarParts";
import { peekKey } from "./peekIntent";
import "./calendar.css";

/** What Tab can reach inside the peek. */
export const FOCUSABLE = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

export type PeekContent =
  | { kind: "post"; post: CalendarPost }
  | { kind: "day"; cell: DayCell; posts: CalendarPost[] };

/** The banner's meta without its "Concept · " lead: one sentence of state. */
function stateSentence(meta: string): string {
  const cut = meta.indexOf(" · ");
  return cut >= 0 ? meta.slice(cut + 3) : meta;
}

// Button / Primary: a 32px accent capsule, 13/500, 14 in.
const primaryClass =
  "inline-flex h-8 items-center gap-2 rounded-pill bg-co-accent-fill px-3.5 text-[13px] font-medium text-co-on-accent outline-none transition-colors hover:bg-co-accent-fill-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent disabled:pointer-events-none disabled:opacity-50";
// Open: text 13/600 with its arrow, 12 in.
const plainClass =
  "inline-flex h-8 items-center gap-1.5 rounded-pill px-3 text-[13px] font-semibold text-co-text outline-none hover:bg-co-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent";
const iconGhost =
  "grid place-items-center rounded-pill text-co-muted outline-none hover:bg-co-hover hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent";

/**
 * PostPeek (DESIGN.md "The Calendar in Parts"): what a post is, whose turn it
 * is, one action and Open, anchored to the card it grew from. It shows on hover
 * (after a short intent delay) or keyboard focus, non-modal, so the card keeps
 * focus and a click on the card opens the post. A day with more posts than fit
 * opens here as its list, modal, from a click.
 */
export default function PostPeek({
  triggerRef,
  content,
  isGuest,
  onClose,
  onOpenPost,
  onPickPost,
  primaryFor,
  onPrimary,
  onMove,
  onDelete,
  placement = "end top",
  deleteBlocked,
  nonModal = false,
  onPointerEnter,
  onPointerLeave,
  onReturnToCard,
  onFocusLeave,
}: {
  /** Escape, or Shift+Tab from the first control: focus goes back to the card. */
  onReturnToCard?: () => void;
  /** Focus left the peek for somewhere other than its card. */
  onFocusLeave?: (next: Element | null) => void;
  /** A hover or focus peek: no focus trap and no underlay; the page closes it. */
  nonModal?: boolean;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  /** A post mid-run or mid-publish cannot be deleted (slotDeleteBlocked). */
  deleteBlocked?: (post: CalendarPost) => boolean;
  /** Beside a card in Month and Week; under the row in List. */
  placement?: "end top" | "bottom start";
  triggerRef: RefObject<HTMLElement | null>;
  content: PeekContent | null;
  isGuest: boolean;
  onClose: () => void;
  onOpenPost: (post: CalendarPost) => void;
  onPickPost: (post: CalendarPost) => void;
  /** Whether the postState primary can run from the calendar. */
  primaryFor: (action: PrimaryAction, post: CalendarPost) => boolean;
  onPrimary: (action: PrimaryAction, post: CalendarPost) => void;
  onMove?: (post: CalendarPost) => void;
  onDelete?: (post: CalendarPost) => void;
}) {
  const portal = useCoShellPortal();
  const label = content?.kind === "post" ? content.post.slot.title : "Posts on this day";
  // Calendar / Post peek: 420 wide, radius 12, floating chrome (tier 1).
  const boxClass = "co-glass-chrome relative w-[420px] max-w-[calc(100vw-2rem)] rounded-card text-co-text outline-none";
  // A hover peek must not take focus from the card, so it is a plain region
  // rather than a Dialog (which focuses itself on mount).
  const box = (children: ReactNode) =>
    nonModal ? (
      <div
        role="group"
        aria-label={label}
        data-calendar-peek=""
        className={boxClass}
        onKeyDown={(event) => {
          const first = event.currentTarget.querySelector<HTMLElement>(FOCUSABLE);
          const action = peekKey({ key: event.key, shift: event.shiftKey, where: "peek", atFirst: event.target === first });
          if (action !== "to-card" || !onReturnToCard) return;
          event.preventDefault();
          onReturnToCard();
        }}
        onBlur={(event) => {
          const next = event.relatedTarget as Element | null;
          if (next && event.currentTarget.contains(next)) return;
          onFocusLeave?.(next);
        }}
      >
        {children}
      </div>
    ) : (
      <Dialog aria-label={label} data-calendar-peek="" className={boxClass}>
        {children}
      </Dialog>
    );
  return (
    <Popover
      triggerRef={triggerRef}
      isOpen={!!content}
      isNonModal={nonModal}
      onOpenChange={(open) => !open && onClose()}
      placement={placement}
      // Pen: 12 off the card, its top 13 above the card's.
      offset={placement === "end top" ? 12 : 10}
      crossOffset={placement === "end top" ? -13 : 0}
      shouldFlip
      UNSTABLE_portalContainer={portal ?? undefined}
      className="co-cal-peek z-[var(--z-index-popover)] outline-none"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      {box(
        <>
        {content?.kind === "post" ? (
          <PostBody
            key={content.post.slot.id}
            post={content.post}
            isGuest={isGuest}
            onClose={onClose}
            onOpenPost={onOpenPost}
            primaryFor={primaryFor}
            onPrimary={onPrimary}
            onMove={onMove}
            onDelete={onDelete}
            deleteBlocked={deleteBlocked?.(content.post) ?? false}
          />
        ) : content?.kind === "day" ? (
          <div className="flex flex-col">
            <header className="flex items-center justify-between gap-3 px-[22px] pb-2 pt-5">
              <h2 className="font-section text-[21px] font-bold leading-[1.12] text-co-text">
                {dayMonthShort(content.cell)} · {content.posts.length} posts
              </h2>
              <button type="button" aria-label="Close" onClick={onClose} className={cn(iconGhost, "size-7")}>
                <X className="size-4" aria-hidden="true" />
              </button>
            </header>
            <ul className="flex max-h-[420px] flex-col gap-1 overflow-y-auto px-3 pb-3">
              {content.posts.map((post) => {
                const hue = pillarHue(post.pillarIndex);
                return (
                  <li key={post.slot.id}>
                    <button
                      type="button"
                      onClick={() => onPickPost(post)}
                      className="relative flex w-full flex-col gap-[7px] overflow-hidden rounded-card px-2.5 pb-2.5 pt-[9px] text-left hover:bg-co-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent"
                    >
                      {hue ? <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px]" style={{ background: hue }} /> : null}
                      <span className="line-clamp-2 text-[13px] font-semibold leading-[1.32] text-co-text">{post.slot.title}</span>
                      <TurnLine owner={post.state.turn.owner} label={post.state.turn.label} alert={post.state.turn.alert} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        </>,
      )}
    </Popover>
  );
}

function PostBody({
  post,
  isGuest,
  onClose,
  onOpenPost,
  primaryFor,
  onPrimary,
  onMove,
  onDelete,
  deleteBlocked,
}: {
  deleteBlocked: boolean;
  post: CalendarPost;
  isGuest: boolean;
  onClose: () => void;
  onOpenPost: (post: CalendarPost) => void;
  primaryFor: (action: PrimaryAction, post: CalendarPost) => boolean;
  onPrimary: (action: PrimaryAction, post: CalendarPost) => void;
  onMove?: (post: CalendarPost) => void;
  onDelete?: (post: CalendarPost) => void;
}) {
  const [menu, setMenu] = useState(false);
  const { slot, state } = post;
  const primary = state.banner.primary;
  const showPrimary = !!primary && !primary.disabled && primaryFor(primary.action, post);
  const hasMenu = !!onMove || !!onDelete;
  const at = new Date(slot.scheduledAt);
  const day = { weekday: at.getUTCDay(), day: at.getUTCDate(), month: at.getUTCMonth() + 1 };
  const format = formatLabel(slot);
  // "Tue 15 Dec · video 9:16"
  const meta = `${dayMonthShort(day)} · ${format.charAt(0).toLowerCase()}${format.slice(1)}`;
  return (
    <div className="flex flex-col gap-3.5 px-[22px] py-5">
      <header className="flex items-start gap-3">
        <h2 className="min-w-0 flex-1 font-section text-[21px] font-bold leading-[1.12] text-co-text">{slot.title}</h2>
        <span className="relative flex shrink-0 items-center">
          {hasMenu ? (
            <button
              type="button"
              aria-label="More"
              aria-expanded={menu}
              onClick={() => setMenu((open) => !open)}
              className={cn(iconGhost, "size-[30px]")}
            >
              <MoreHorizontal className="size-[15px]" aria-hidden="true" />
            </button>
          ) : null}
          <button type="button" aria-label="Close" onClick={onClose} className={cn(iconGhost, "size-7")}>
            <X className="size-4" aria-hidden="true" />
          </button>
          {menu ? (
            <span role="menu" className="co-glass-chrome absolute right-0 top-9 z-10 flex w-[240px] flex-col rounded-card p-1.5">
              {onMove ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenu(false);
                    onMove(post);
                  }}
                  className="flex h-9 items-center gap-2.5 rounded-row px-2.5 text-left text-[13px] font-medium text-co-text hover:bg-co-hover"
                >
                  <CalendarDays className="size-3.5 text-co-muted" aria-hidden="true" />
                  Move to another day
                </button>
              ) : null}
              {onDelete ? (
                <button
                  type="button"
                  role="menuitem"
                  disabled={deleteBlocked}
                  aria-disabled={deleteBlocked || undefined}
                  onClick={() => {
                    if (deleteBlocked) return;
                    setMenu(false);
                    onDelete(post);
                  }}
                  className="flex min-h-9 items-center gap-2.5 rounded-row px-2.5 py-1.5 text-left text-[13px] font-medium text-co-alert-ink hover:bg-co-hover disabled:cursor-not-allowed disabled:text-co-muted disabled:hover:bg-transparent"
                >
                  <Trash2 className="size-3.5 shrink-0" aria-hidden="true" />
                  {deleteBlocked ? "Delete post (unavailable while it is working)" : "Delete post"}
                </button>
              ) : null}
            </span>
          ) : null}
        </span>
      </header>

      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-[12.5px] text-co-muted">{meta}</span>
        <ChannelMarks channels={post.channels} size="md" className="gap-2" />
      </div>

      <div className="flex flex-col gap-2">
        <StageTrack stageIndex={state.stageIndex} stops={state.stops} size="card" />
        <span className="flex min-w-0 items-center gap-2 text-[13px]">
          <span className="font-semibold text-co-text">{stageWord(state, isGuest)}</span>
          <span className="min-w-0 truncate text-co-text2">{state.turn.label}</span>
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-[16px] font-semibold text-co-text">{state.banner.title}</p>
        <p className="text-[13.5px] leading-[1.45] text-co-text2">{stateSentence(state.banner.meta)}</p>
      </div>

      <div className="flex items-center gap-2.5">
        {showPrimary && primary ? (
          <button type="button" className={primaryClass} onClick={() => onPrimary(primary.action, post)}>
            {primary.label}
          </button>
        ) : null}
        <span className="flex-1" />
        <button type="button" className={plainClass} onClick={() => onOpenPost(post)}>
          Open
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
